import { createTokenStorage } from './tokenStorage'
import type { ApiClient, ApiClientOptions, FetchImplementation, RequestContext, RequestOptions, ResponseContext } from './types'

const isFormData = (value: unknown): value is FormData => typeof FormData !== 'undefined' && value instanceof FormData

const sleep = (ms: number) => new Promise((resolve) => {
  setTimeout(resolve, ms)
})

const defaultLogger = {
  debug: (...args: unknown[]) => {
    if (import.meta.env?.DEV) {
      console.debug('[api-client]', ...args)
    }
  },
  error: (...args: unknown[]) => console.error('[api-client]', ...args),
}

const ensureJson = (body: unknown) => (typeof body === 'string' ? body : JSON.stringify(body))

const parseResponseData = async <T>(response: Response, responseType: RequestOptions['responseType'] = 'json'): Promise<T> => {
  if (responseType === 'blob') {
    return response.blob() as unknown as T
  }

  if (responseType === 'text') {
    return response.text() as unknown as T
  }

  if (response.status === 204) {
    return undefined as unknown as T
  }

  return response.json() as Promise<T>
}

const mergeHeaders = (source: HeadersInit | undefined, target: Record<string, string>) => {
  if (!source) {
    return target
  }

  const result: Record<string, string> = { ...target }

  const entries = source instanceof Headers
    ? Array.from(source.entries())
    : Array.isArray(source)
      ? source
      : Object.entries(source)

  for (const [key, value] of entries) {
    result[key.toLowerCase()] = value
  }

  return result
}

const createRequestInit = (init: RequestInit = {}): RequestInit => {
  const result: RequestInit = { ...init }
  if (init.headers instanceof Headers) {
    result.headers = new Headers(init.headers)
  } else if (Array.isArray(init.headers)) {
    result.headers = [...init.headers]
  } else if (init.headers) {
    result.headers = { ...init.headers }
  }

  return result
}

export const createApiClient = (options: ApiClientOptions = {}): ApiClient => {
  const fetchImpl: FetchImplementation = options.fetchImplementation ?? fetch
  const tokenStorage = options.tokenStorage ?? createTokenStorage()
  const logger = {
    ...defaultLogger,
    ...options.logger,
  }

  const baseURL = options.baseURL ?? '/api'

  const runWithRetry = async <T>(fn: () => Promise<T>, retries = 0, delayMs = 200): Promise<T> => {
    try {
      return await fn()
    } catch (error) {
      if (retries <= 0) {
        throw error
      }

      await sleep(delayMs)
      return runWithRetry(fn, retries - 1, delayMs * 2)
    }
  }

  const enrichRequest = (endpoint: string, init: RequestInit, opts: RequestOptions = {}) => {
    const headers = mergeHeaders(init.headers, {})

    if (!opts.skipAuth) {
      const token = tokenStorage.getToken()
      if (token) {
        headers['x-auth'] = token
      }
    }

    if (options.spaceId) {
      headers['x-space-id'] = options.spaceId
    }

    if (options.expectFormat ?? true) {
      headers['x-expect-format'] = options.expectFormat ?? 'json'
    }

    if (!isFormData(init.body) && !headers['content-type']) {
      headers['content-type'] = 'application/json'
    }

    const finalHeaders = new Headers()
    Object.entries({
      ...(options.defaultHeaders ?? {}),
      ...headers,
    }).forEach(([key, value]) => {
      finalHeaders.set(key, value)
    })

    const requestInit: RequestInit = {
      ...init,
      headers: finalHeaders,
    }

    if (!isFormData(init.body) && init.body && typeof init.body === 'object' && !(init.body instanceof URLSearchParams)) {
      requestInit.body = ensureJson(init.body)
    }

    const effectiveBaseURL = opts.baseURL ?? baseURL
    const url = endpoint.startsWith('http') ? endpoint : `${effectiveBaseURL}${endpoint}`

    return { requestInit, url }
  }

  const handleResponse = async <T>(context: ResponseContext<T>) => {
    const { response } = context

    const nextToken = response.headers.get('x-token') ?? response.headers.get('x-auth')
    if (nextToken) {
      tokenStorage.setToken(nextToken)
      options.onTokenChange?.(nextToken)
    }

    if (!response.ok) {
      if (response.status === 401) {
        tokenStorage.clearToken()
        options.onTokenChange?.(null)
        options.onUnauthorized?.()
      }

      throw Object.assign(new Error(`HTTP error! status: ${response.status}`), {
        response,
        request: context,
      })
    }

    return context
  }

  const execute = async <T>(endpoint: string, opts: RequestOptions = {}) => {
    const { baseURL: overrideBaseURL, ...restOptions } = opts
    const init = createRequestInit(restOptions)

    const { requestInit, url } = enrichRequest(endpoint, init, {
      ...restOptions,
      baseURL: overrideBaseURL,
    })

    const requestContext: RequestContext = {
      endpoint,
      url,
      init: requestInit,
    }

    logger.debug?.('request', requestContext)

    const performRequest = async () => {
      const response = await fetchImpl(url, requestInit)
      const responseContext: ResponseContext<T> = {
        ...requestContext,
        response,
      }

      const processed = await handleResponse(responseContext)

      if (opts.parseResponse === false) {
        return { data: undefined, context: processed }
      }

      const data = await parseResponseData<T>(processed.response, opts.responseType)
      const finalContext = { ...processed, data }
      logger.debug?.('response', finalContext)

      return { data, context: finalContext }
    }

    const retries = opts.retry ?? 0
    const retryDelay = opts.retryDelayMs ?? 200
    const result = await runWithRetry(performRequest, retries, retryDelay)

    return result
  }

  const requestRaw = async <T>(endpoint: string, options?: RequestOptions) => {
    const { data, context } = await execute<T>(endpoint, options)
    return { data: data as T, response: context.response }
  }

  const request = async <T>(endpoint: string, options?: RequestOptions) => {
    const { data } = await execute<T>(endpoint, options)
    return data as T
  }

  const requestWithBody = async <T>(method: RequestInit['method'], endpoint: string, body?: unknown, options?: RequestOptions) => {
    return request<T>(endpoint, {
      ...options,
      method,
      body: body as any,
    })
  }

  return {
    request,
    requestRaw,
    get: (endpoint, options) => request(endpoint, { ...options, method: 'GET' }),
    post: (endpoint, body, options) => requestWithBody('POST', endpoint, body, options),
    put: (endpoint, body, options) => requestWithBody('PUT', endpoint, body, options),
    patch: (endpoint, body, options) => requestWithBody('PATCH', endpoint, body, options),
    delete: (endpoint, options) => request(endpoint, { ...options, method: 'DELETE' }),
    clearToken: () => {
      tokenStorage.clearToken()
      options.onTokenChange?.(null)
    },
    setToken: (token: string) => {
      tokenStorage.setToken(token)
      options.onTokenChange?.(token)
    },
    getToken: () => tokenStorage.getToken(),
  }
}


